import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Star, Check, X } from 'lucide-react';
import { usePendingReviews, useApproveReview, useRejectReview } from '@/hooks/useUserReviews';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';

/**
 * Admin Review Moderation Queue
 * Approve or reject pending user reviews
 */
export default function ReviewModeration() {
  const { isAdmin } = useAuth();
  const { data: reviews, isLoading } = usePendingReviews();
  const approveReview = useApproveReview();
  const rejectReview = useRejectReview();

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold">Review Moderation</h1>
              <p className="text-muted-foreground mt-1">
                Approve or reject pending user reviews
              </p>
            </div>
            <Link to="/admin">
              <Button variant="outline">← Back to Admin</Button>
            </Link>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48 w-full" />
            ))}
          </div>
        ) : !reviews || reviews.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <p className="text-muted-foreground">No pending reviews at this time.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => {
              const initials = review.user_full_name
                ?.split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase() || review.user_email?.[0]?.toUpperCase() || '?';

              const timeAgo = formatDistanceToNow(new Date(review.created_at), { addSuffix: true });

              return (
                <Card key={review.id} className="border-2 border-orange-200">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Avatar>
                          <AvatarImage src={review.user_avatar_url || undefined} />
                          <AvatarFallback>{initials}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">
                            {review.user_full_name || 'Anonymous'}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">
                            {review.user_email}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <span>{timeAgo}</span>
                            <Badge variant="outline" className="bg-orange-100 text-orange-800">
                              Pending
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Rating Stars */}
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-5 w-5 ${
                              star <= review.rating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-muted-foreground'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Review Content */}
                    <div>
                      <h3 className="font-semibold text-lg mb-2">{review.title}</h3>
                      <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                        {review.content}
                      </p>
                    </div>

                    {/* Product Info */}
                    <div className="pt-4 border-t">
                      <p className="text-sm text-muted-foreground mb-2">Product ID:</p>
                      <code className="text-xs bg-muted px-2 py-1 rounded">{review.product_id}</code>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4 border-t">
                      <Button
                        onClick={() => approveReview.mutate(review.id)}
                        disabled={approveReview.isPending || rejectReview.isPending}
                        variant="default"
                        className="flex-1"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => rejectReview.mutate(review.id)}
                        disabled={approveReview.isPending || rejectReview.isPending}
                        variant="destructive"
                        className="flex-1"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject & Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
